import { Constants, Colors } from 'utils';

export type RevisionRole = 'client' | 'lawyer';

// ── Datos derivados compartidos entre RevisionTableRow (escritorio) y
// RevisionCard (mobile) ──────────────────────────────────────────────────
// Antes esta logica vivia duplicada dentro de cada fila; al separar la
// tabla de escritorio y la tarjeta de mobile en dos componentes, se extrae
// aqui para que ambos lean exactamente las mismas reglas de negocio y no
// puedan desincronizarse entre si.

export const getRevisionStatusColor = (item: any) => {
  switch (item.status) {
    case Constants.REVISION_STATUS.PENDING:
      return Colors.orange;
    case Constants.REVISION_STATUS.ACCEPTED:
      return Colors.green;
    case Constants.REVISION_STATUS.REJECTED:
      return Colors.danger;
    case Constants.REVISION_STATUS.FINISHED:
      return Colors.gray3;
    case Constants.REVISION_STATUS.SEARCHING:
      return Colors.blue;
    case Constants.REVISION_STATUS.NO_TAKEN:
      return Colors.blue;
    case Constants.REVISION_STATUS.CANCELLED:
      return Colors.gray6;
  }
};

// Whitelist invertida: coincide con los estados terminales que el backend excluye
// en RevisionService.cancelRevision() (FINISHED, REJECTED, NO_TAKEN, CANCELLED).
const REVISION_TERMINAL_STATUSES = [
  Constants.REVISION_STATUS.FINISHED,
  Constants.REVISION_STATUS.REJECTED,
  Constants.REVISION_STATUS.NO_TAKEN,
  Constants.REVISION_STATUS.CANCELLED,
];

// El backend también bloquea si ya hay archivos intercambiados en el chat de
// comentarios (no visible desde el listado) -- esa validación final la hace
// siempre el backend; acá solo se aproxima con lo que el listado expone
// (file_response, la respuesta formal del abogado).
export const canCancelRevision = (item: any) =>
  !REVISION_TERMINAL_STATUSES.includes(item.status) && item.file_response == null;

// Autorización: dueño (cliente) o abogado EXTERNO asignado -- coincide con
// resolveCancellationAuthorization() del backend. El abogado interno no puede
// cancelar.
export const canShowCancelRevision = (item: any, role: RevisionRole) =>
  canCancelRevision(item) && (role === 'client' || !item.internal_assignament);

export interface RevisionRowData {
  is_client: boolean;
  counterpart: any;
  counterpartLabel: string;
  noCounterpartText: string;
  statusText: string;
  color: string | undefined;
  // Muestra el bloque de descargar/subir + responder archivo.
  showFileActions: boolean;
  // Muestra el acceso a "Comentarios" (solo si ya hay contraparte asignada).
  showComments: boolean;
  // Boton "Culminar" (solo abogado).
  showFinishButton: boolean;
  // DESHABILITADO: nuevo flujo de revision documental -- el cliente ya no
  // aprueba/rechaza el archivo. Se conserva comentado (no se elimina) por
  // si se requiere revertir.
  // Botones "Aceptar" / "Rechazar" (solo cliente).
  showAcceptReject: boolean;
  // Boton "Calificar" (solo cliente, cuando el abogado ya culmino).
  showRateButton: boolean;
  // Icono de 3 puntos + "Cancelar" en la celda de fecha/ID (cliente o abogado
  // externo asignado, mientras no haya archivos intercambiados y el estado no
  // sea terminal). Independiente de Culminar/Calificar: en la practica nunca
  // coinciden (Culminar exige PENDING, Calificar exige FINISHED que ya es
  // terminal), pero conceptualmente ya no "reemplaza" nada -- vive en su
  // propia celda.
  showCancelButton: boolean;
}

export const getRevisionRowData = (item: any, role: RevisionRole): RevisionRowData => {
  const is_client = role === 'client';
  const counterpart = is_client ? item.lawyer : item.user;
  const counterpartLabel = is_client ? 'Abogado' : 'Cliente';
  const noCounterpartText = is_client ? 'No se ha asignado abogado' : 'No se ha asignado cliente';

  const statusText = is_client
    ? [Constants.REVISION_STATUS.SEARCHING, Constants.REVISION_STATUS.NO_TAKEN].includes(
        item.status,
      )
      ? 'Buscando abogado'
      : item.status_text
    : item.status_text;

  const showFileActions =
    !is_client ||
    ![Constants.REVISION_STATUS.SEARCHING, Constants.REVISION_STATUS.NO_TAKEN].includes(
      item.status,
    );

  const showComments = counterpart != null;

  const showFinishButton =
    !is_client &&
    item.status === Constants.REVISION_STATUS.PENDING &&
    item.file != null &&
    item.file_response != null;

  // DESHABILITADO: nuevo flujo de revision documental -- el cliente ya no
  // aprueba/rechaza el archivo manualmente. Se conserva comentado (no se
  // elimina) por si se requiere revertir.
  // const showAcceptReject =
  //   is_client &&
  //   item.status == Constants.SIGNATURE_STATUS.PENDING &&
  //   item.file_response != null;
  const showAcceptReject = false;

  const showRateButton =
    is_client && item.status === Constants.REVISION_STATUS.FINISHED && item.rating == null;

  const showCancelButton = canShowCancelRevision(item, role);

  return {
    is_client,
    counterpart,
    counterpartLabel,
    noCounterpartText,
    statusText,
    color: getRevisionStatusColor(item),
    showFileActions,
    showComments,
    showFinishButton,
    showAcceptReject,
    showRateButton,
    showCancelButton,
  };
};
